import React, { useEffect, useState, useCallback } from 'react';
import { telnyxNumbersApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Plus, Trash2, Phone, RefreshCw, ShoppingCart, Search } from 'lucide-react';
import { toast } from 'sonner';
import ConnectionsSubNav from '../../components/hub/ConnectionsSubNav';

const STATUS_COLORS = {
  available: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  assigned: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200',
  released: 'bg-zinc-200 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300',
};

export default function PhoneNumbersPage() {
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await telnyxNumbersApi.list();
      setNumbers(data || []);
    } catch (e) {
      toast.error(`Failed to load phone numbers: ${e.message || e}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (id, label) => {
    if (!window.confirm(`Remove number "${label}"?`)) return;
    try {
      await telnyxNumbersApi.delete(id);
      toast.success('Phone number removed');
      load();
    } catch (e) {
      toast.error(`Failed to delete: ${e.message || e}`);
    }
  };

  return (
    <div data-testid="phone-numbers-page">
      <ConnectionsSubNav />
      <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Phone className="w-7 h-7" />
            Telnyx Phone Numbers
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage platform phone numbers. Each number can be assigned to one or more exercises.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="refresh-numbers-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button variant="outline" onClick={() => setSearchOpen(true)} data-testid="search-numbers-btn">
            <Search className="w-4 h-4 mr-2" /> Search Telnyx Inventory
          </Button>
          <Button onClick={() => setAddOpen(true)} data-testid="add-number-btn">
            <Plus className="w-4 h-4 mr-2" /> Add Number
          </Button>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Label</TableHead>
              <TableHead>Number</TableHead>
              <TableHead>Country</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Assigned to Exercises</TableHead>
              <TableHead>Purchased</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : numbers.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground">No phone numbers yet. Click "Add Number" or "Search Telnyx Inventory" to get started.</TableCell></TableRow>
            ) : numbers.map((n) => (
              <TableRow key={n.phone_number_id} data-testid={`row-number-${n.phone_number_id}`}>
                <TableCell className="font-medium">{n.label || n.phone_number}</TableCell>
                <TableCell className="font-mono text-sm">{n.phone_number}</TableCell>
                <TableCell>{n.country || '—'}</TableCell>
                <TableCell>
                  <Badge className={STATUS_COLORS[n.status] || ''}>{n.status}</Badge>
                </TableCell>
                <TableCell className="text-xs">
                  {(n.assigned_exercise_ids || []).length === 0
                    ? <span className="text-muted-foreground">none</span>
                    : `${n.assigned_exercise_ids.length} exercise${n.assigned_exercise_ids.length !== 1 ? 's' : ''}`}
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">
                  {n.purchased_at ? new Date(n.purchased_at).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleDelete(n.phone_number_id, n.label || n.phone_number)}
                    data-testid={`delete-number-${n.phone_number_id}`}
                    disabled={(n.assigned_exercise_ids || []).length > 0}
                    title={(n.assigned_exercise_ids || []).length > 0 ? 'Unassign from exercises first' : 'Delete'}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <AddNumberDialog open={addOpen} onClose={() => setAddOpen(false)} onSaved={load} />
      <SearchTelnyxDialog open={searchOpen} onClose={() => setSearchOpen(false)} onPurchased={load} />
      </div>
    </div>
  );
}


function AddNumberDialog({ open, onClose, onSaved }) {
  const [phone, setPhone] = useState('');
  const [label, setLabel] = useState('');
  const [country, setCountry] = useState('GB');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!phone.trim()) { toast.error('Phone number required'); return; }
    setSaving(true);
    try {
      await telnyxNumbersApi.create({ phone_number: phone.trim(), label: label.trim(), country });
      toast.success('Phone number registered');
      setPhone(''); setLabel(''); setCountry('GB');
      onSaved();
      onClose();
    } catch (e) {
      toast.error(`Failed: ${e.message || e}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="add-number-dialog">
        <DialogHeader>
          <DialogTitle>Add Phone Number</DialogTitle>
          <DialogDescription>
            Manually register a phone number you already own with Telnyx.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Phone Number (E.164)</Label>
            <Input
              placeholder="+442382548253"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="add-number-phone-input"
            />
          </div>
          <div>
            <Label>Label (optional)</Label>
            <Input
              placeholder="e.g. Coast Guard SimCell"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              data-testid="add-number-label-input"
            />
          </div>
          <div>
            <Label>Country</Label>
            <Input
              placeholder="GB"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              data-testid="add-number-country-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="add-number-submit">
            {saving ? 'Saving...' : 'Add Number'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function SearchTelnyxDialog({ open, onClose, onPurchased }) {
  const [country, setCountry] = useState('GB');
  const [areaCode, setAreaCode] = useState('');
  const [contains, setContains] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  const handleSearch = async () => {
    setSearching(true);
    setResults([]);
    try {
      const r = await telnyxNumbersApi.search({ country, area_code: areaCode || undefined, contains: contains || undefined, limit: 10 });
      setResults((r && r.data) || []);
      if (!((r && r.data) || []).length) toast.info('No numbers found — try a different search');
    } catch (e) {
      toast.error(`Search failed: ${e.message || e}`);
    } finally {
      setSearching(false);
    }
  };

  const handlePurchase = async (pn) => {
    if (!window.confirm(`Purchase ${pn} from Telnyx?`)) return;
    setPurchasing(true);
    try {
      await telnyxNumbersApi.purchase({ phone_numbers: [pn] });
      toast.success(`Purchased ${pn}`);
      onPurchased();
      onClose();
    } catch (e) {
      toast.error(`Purchase failed: ${e.message || e}`);
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="search-telnyx-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Search Telnyx Inventory</DialogTitle>
          <DialogDescription>Find available numbers and purchase them directly.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Country</Label>
            <Input value={country} onChange={(e) => setCountry(e.target.value)} placeholder="GB" />
          </div>
          <div>
            <Label>Area Code</Label>
            <Input value={areaCode} onChange={(e) => setAreaCode(e.target.value)} placeholder="023" />
          </div>
          <div>
            <Label>Contains</Label>
            <Input value={contains} onChange={(e) => setContains(e.target.value)} placeholder="123" />
          </div>
        </div>
        <Button onClick={handleSearch} disabled={searching} data-testid="search-telnyx-submit">
          <Search className="w-4 h-4 mr-2" />
          {searching ? 'Searching...' : 'Search'}
        </Button>

        <div className="border rounded-md max-h-72 overflow-y-auto">
          {results.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No results yet</div>
          ) : results.map((r) => (
            <div key={r.phone_number || r.id} className="flex items-center justify-between p-3 border-b last:border-b-0">
              <div className="flex flex-col">
                <span className="font-mono text-sm">{r.phone_number}</span>
                <span className="text-xs text-muted-foreground">
                  {r.region_information?.[0]?.region_name || r.country_code || ''} · {r.cost_information?.monthly_cost ? `$${r.cost_information.monthly_cost}/mo` : ''}
                </span>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handlePurchase(r.phone_number)}
                disabled={purchasing}
              >
                <ShoppingCart className="w-3.5 h-3.5 mr-1.5" /> Buy
              </Button>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
